/* Keeps native Printify design references intact. No preview text is submitted as artwork. */
(() => {
  'use strict';
  const notice = document.querySelector('[data-oryn-printify-guard]');
  if (!notice || notice.dataset.initialized === 'true') return;
  notice.dataset.initialized = 'true';
  const message = notice.querySelector('[data-guard-message]');
  const openPersonalizer = notice.querySelector('[data-open-personalizer]');
  const reviewCart = notice.querySelector('[data-review-cart]');
  const productProtected = notice.dataset.protectProduct === 'true';
  const productId = notice.dataset.productId || '';
  const fixedSalePolicy = notice.dataset.fixedSalePolicy || '';
  const comforterOnly = fixedSalePolicy === 'COMFORTER_416_FIXED_PRINT';
  const legacyProductIds = new Set(['15738749616452', '15738786152772']);
  const legacyVariantIds = new Set(['57532439200068', '57532636135748']);
  const fixedPairs = new Set(), fixedProductIds = new Set(), fixedVariantIds = new Set();
  let configurationValid = true;
  const variantIds = new Set(legacyVariantIds);
  try {
    if (fixedSalePolicy && !comforterOnly) throw new Error('Invalid sale policy');
    const ids = JSON.parse(notice.dataset.protectedVariantIds || '[]');
    if (!Array.isArray(ids) || ids.some(id => !/^\d+$/.test(String(id)))) throw new Error('Invalid production references');
    ids.forEach(id => variantIds.add(String(id)));
    if (productId && !/^\d{10,20}$/.test(productId)) throw new Error('Invalid product reference');
    if (productProtected && !productId) throw new Error('Missing product reference');
    const pairs = JSON.parse(notice.dataset.fixedProductPairs);
    if (!Array.isArray(pairs)) throw new Error('Invalid fixed-print references');
    for (const pair of pairs) {
      if (!pair || typeof pair !== 'object' || Array.isArray(pair) || Object.keys(pair).length !== 2 ||
        typeof pair.productId !== 'string' || !/^\d{10,20}$/.test(pair.productId) ||
        typeof pair.id !== 'string' || !/^\d{10,20}$/.test(pair.id) ||
        legacyProductIds.has(pair.productId) || legacyVariantIds.has(pair.id) || fixedVariantIds.has(pair.id)) {
        throw new Error('Invalid fixed-print references');
      }
      fixedPairs.add(pair.productId + ':' + pair.id);
      fixedProductIds.add(pair.productId);
      fixedVariantIds.add(pair.id);
    }
  } catch {
    configurationValid = false;
  }
  const configuredRoot = notice.dataset.root || '/';
  const routeRoot = /^\/(?:[a-zA-Z0-9_-]+\/?)*$/.test(configuredRoot) ? configuredRoot.replace(/\/?$/, '/') : '/';
  const cartUrl = new URL(routeRoot + 'cart', location.origin);
  if (reviewCart) reviewCart.href = cartUrl.href;
  const addSelector = 'button[name="add"], .sticky-add-to-cart__button';
  const originalAria = new WeakMap();
  const approvedForms = new WeakSet();
  let checkoutBusy = false;
  let verificationRun = 0;
  let activeController = null;
  let activeTrigger = null;
  let checkoutError = false;
  let scheduled = false;

  function personalizationFields(form) {
    return form ? Array.from(form.elements).filter(field => field.name === 'properties[Personalization ID]' && !field.disabled) : [];
  }
  function savedDesign(form) {
    const values = personalizationFields(form).map(field => String(field.value || '').trim());
    return values.length > 0 && values.every(value => value !== '' && value === values[0]);
  }
  function forms() {
    return Array.from(document.querySelectorAll('form[action*="/cart/add"]'));
  }
  function requiresDesign(form) {
    const variantId = String(form?.elements.namedItem('id')?.value || '');
    return variantIds.has(variantId) || ((productProtected || legacyProductIds.has(productId)) && form === mainForm());
  }
  function mainForm() {
    return forms().find(form => form.querySelector('[data-testid="standalone-add-to-cart"]')) || forms()[0];
  }
  function hubButton() {
    return document.querySelector('button.personalize-button');
  }
  function showMessage(text, cartError = false, anchor = null) {
    if (!message) return;
    if (anchor?.isConnected) anchor.before(notice);
    else if (!notice.isConnected) document.body.append(notice);
    if (message.textContent !== text) message.textContent = text;
    notice.hidden = false;
    if (openPersonalizer) openPersonalizer.hidden = cartError || !hubButton();
    if (reviewCart) reviewCart.hidden = !cartError;
  }
  function stop(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
  function productProblem(form) {
    if (!configurationValid) return 'Product references could not be verified. Please reload before adding this item.';
    if (comforterOnly && notice.dataset.checkoutReady !== 'true') return 'Orders are not open. You can continue to preview your bedding.';
    const variantId = String(form?.elements.namedItem('id')?.value || '');
    if (fixedPairs.has(productId + ':' + variantId)) {
      return personalizationFields(form).some(field => String(field.value ?? '').trim() !== '')
        ? 'Fixed-print bedding cannot use a saved personalized design. Remove that design and add the fixed-print item again.' : null;
    }
    if (comforterOnly) return 'Only the available fixed-print comforters in this collection can be ordered. Cotton pieces are preview only.';
    if (fixedVariantIds.has(variantId) || (fixedProductIds.has(productId) && form === mainForm())) {
      return 'This fixed-print product and variant could not be verified. Please reload its product page before adding it.';
    }
    return requiresDesign(form) && !savedDesign(form)
      ? notice.dataset.textConfirm || 'Confirm and save your design before adding this item. For no initials, remove the example letters and save.' : null;
  }
  function blockProduct(event, form) {
    const problem = productProblem(form);
    if (!problem) return false;
    stop(event);
    checkoutError = false;
    showMessage(problem, false, form);
    if (openPersonalizer && (!configurationValid || fixedProductIds.has(productId))) openPersonalizer.hidden = true;
    hubButton()?.focus();
    return true;
  }
  function synchronize() {
    scheduled = false;
    for (const button of document.querySelectorAll(addSelector)) {
      const form = button.form || mainForm();
      const locked = Boolean(productProblem(form));
      if (locked) {
        if (!originalAria.has(button)) originalAria.set(button, button.getAttribute('aria-disabled'));
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('oryn-design-unconfirmed');
      } else if (originalAria.has(button)) {
        const before = originalAria.get(button);
        if (before === null) button.removeAttribute('aria-disabled');
        else button.setAttribute('aria-disabled', before);
        originalAria.delete(button);
        button.classList.remove('oryn-design-unconfirmed');
      }
    }
    if (!checkoutError && !productProblem(mainForm())) notice.hidden = true;
  }
  function scheduleSync() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(synchronize);
  }
  async function checkedCart(controller) {
    if (!configurationValid) throw new Error('Production references are unavailable');
    if (comforterOnly && notice.dataset.checkoutReady !== 'true') return 'Orders are not open. You can continue to preview your bedding.';
    const timer = setTimeout(() => controller.abort(), 7000);
    try {
      const response = await fetch(routeRoot + 'cart.js', {credentials: 'same-origin', cache: 'no-store', headers: {Accept: 'application/json'}, signal: controller.signal});
      if (!response.ok) throw new Error('Cart verification unavailable');
      const cart = await response.json();
      if (!cart || !Array.isArray(cart.items) || cart.items.some(item =>
        !item || typeof item !== 'object' || Array.isArray(item) ||
        !/^\d+$/.test(String(item.product_id || '')) ||
        !/^\d+$/.test(String(item.variant_id || '')) ||
        !Number.isSafeInteger(item.quantity) || item.quantity < 1 ||
        typeof item.vendor !== 'string' ||
        (item.properties != null && (typeof item.properties !== 'object' || Array.isArray(item.properties)))
      )) throw new Error('Invalid cart response');
      if (cart.items.length === 0) return notice.dataset.textEmpty || 'Your cart is empty. Choose your bedding before checking out.';
      for (const item of cart.items) {
        const itemProductId = String(item.product_id), variantId = String(item.variant_id);
        const design = item.properties?.['Personalization ID'];
        if (fixedPairs.has(itemProductId + ':' + variantId)) {
          if (design != null && (typeof design !== 'string' || design.trim() !== '')) {
            return 'Fixed-print bedding cannot use a saved personalized design. Remove this item and add it again from its fixed-print product page.';
          }
          continue;
        }
        if (comforterOnly) return 'This cart contains an item outside the available fixed-print comforter collection. Remove it before checking out.';
        if (fixedProductIds.has(itemProductId) || fixedVariantIds.has(variantId)) {
          return 'A fixed-print product and variant could not be verified. Remove this item and add it again from its product page.';
        }
        const protectedItem = String(item.vendor || '').trim().toLowerCase() === 'printify' || variantIds.has(variantId) || legacyProductIds.has(itemProductId);
        if (protectedItem && (typeof design !== 'string' || design.trim() === '')) {
          return notice.dataset.textMissing || 'A saved design is missing from an item in your cart. Remove that item, personalize it on its product page, and add it again.';
        }
      }
      return null;
    } finally {
      clearTimeout(timer);
    }
  }
  async function verifyCheckout(proceed, trigger) {
    if (checkoutBusy) return;
    const run = ++verificationRun;
    const controller = new AbortController();
    activeController = controller;
    activeTrigger = trigger;
    checkoutBusy = true;
    trigger?.setAttribute('aria-busy', 'true');
    checkoutError = false;
    try {
      const problem = await checkedCart(controller);
      if (run !== verificationRun) return;
      if (problem) {
        checkoutError = true;
        showMessage(problem, true, trigger);
        return;
      }
      notice.hidden = true;
      proceed();
    } catch {
      if (run !== verificationRun) return;
      checkoutError = true;
      showMessage(notice.dataset.textFailed || 'We could not verify your saved designs. Please try again before checking out.', true, trigger);
    } finally {
      if (run === verificationRun) {
        checkoutBusy = false;
        activeController = null;
        activeTrigger = null;
        trigger?.removeAttribute('aria-busy');
      }
    }
  }
  openPersonalizer?.addEventListener('click', () => hubButton()?.click());
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const add = event.target.closest(addSelector);
    if (add && blockProduct(event, add.form || mainForm())) return;
    const link = event.target.closest('a[href]');
    if (!link) return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || url.pathname !== routeRoot + 'checkout') return;
    stop(event);
    void verifyCheckout(() => location.assign(url.href), link);
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (approvedForms.has(form)) {
      approvedForms.delete(form);
      return;
    }
    const action = new URL(form.action, location.href);
    if (action.origin !== location.origin) return;
    if (action.pathname === routeRoot + 'cart/add' || action.pathname === routeRoot + 'cart/add.js') {
      blockProduct(event, form);
      return;
    }
    const submitter = event.submitter;
    if (submitter?.name !== 'checkout' && action.pathname !== routeRoot + 'checkout') return;
    stop(event);
    void verifyCheckout(() => {
      if (!form.isConnected || (submitter && !submitter.isConnected)) throw new Error('Checkout form changed');
      approvedForms.add(form);
      try { form.requestSubmit(submitter || undefined); }
      finally { approvedForms.delete(form); }
    }, submitter);
  }, true);
  new MutationObserver(scheduleSync).observe(document.documentElement, {subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['value']});
  document.addEventListener('input', scheduleSync, true);
  document.addEventListener('change', scheduleSync, true);
  function resetVerification() {
    verificationRun++;
    activeController?.abort();
    activeController = null;
    activeTrigger?.removeAttribute('aria-busy');
    activeTrigger = null;
    checkoutBusy = false;
  }
  window.addEventListener('pagehide', resetVerification);
  window.addEventListener('pageshow', event => {
    if (event.persisted) resetVerification();
    synchronize();
  });
  synchronize();
})();
